import { useState } from "react";
import { Link } from "react-router-dom";

const inputBase =
  "h-[53px] w-full rounded-[10px] border-0 bg-surface px-5 text-[16px] font-medium text-ink outline-none placeholder:text-ink-placeholder autofill:shadow-[inset_0_0_0_1000px_#edeef3] autofill:[-webkit-text-fill-color:#202020]";

const onlyDigits = (value) => value.replace(/\D/g, "");

// Возвращает текст ошибки или "" если значение корректно
const validators = {
  name: (v) => (v.trim() ? "" : "Укажите имя"),
  organisation: (v) => (v.trim() ? "" : "Укажите компанию"),
  telephone: (v) =>
    onlyDigits(v).length === 10 ? "" : "Введите корректный номер телефона",
  email: (v) =>
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v.trim())
      ? ""
      : "Введите корректный e-mail",
};

const EMPTY_FORM_DATA = {
  name: "",
  organisation: "",
  telephone: "",
  email: "",
};

// Маска (999) 999-99-99, код +7 фиксирован
const formatPhone = (value) => {
  let digits = onlyDigits(value);

  // Срезаем код страны при вставке 11-значного номера
  if (digits.length === 11 && (digits[0] === "7" || digits[0] === "8")) {
    digits = digits.slice(1);
  }
  digits = digits.slice(0, 10);
  if (!digits) return "";

  let formatted = `(${digits.slice(0, 3)}`;
  if (digits.length > 3) formatted += `) ${digits.slice(3, 6)}`;
  if (digits.length > 6) formatted += `-${digits.slice(6, 8)}`;
  if (digits.length > 8) formatted += `-${digits.slice(8, 10)}`;

  return formatted;
};

function Field({ error, children }) {
  return (
    <div>
      {children}
      {error && <p className="mt-1.5 text-[13px] text-red-600">{error}</p>}
    </div>
  );
}

/**
 * Контактная форма для лендингов.
 *   - subject / source — уходят в POST, чтобы пометить источник заявки
 *   - className — позиционирование/наложение
 */
export default function ContactForm({
  subject,
  source,
  title = "Связаться с нами",
  subtitle = "Заполните данные, пожалуйста, чтобы мы могли с вами связаться",
  className = "",
}) {
  const [formData, setFormData] = useState(EMPTY_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === "telephone" ? formatPhone(value) : value;

    setFormData((prev) => ({ ...prev, [name]: nextValue }));

    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleAgreedChange = (e) => {
    setAgreed(e.target.checked);

    if (errors.agreed) setErrors((prev) => ({ ...prev, agreed: "" }));
  };

  const validate = () => {
    const nextErrors = {};

    for (const field of Object.keys(validators)) {
      const msg = validators[field](formData[field]);
      if (msg) nextErrors[field] = msg;
    }

    if (!agreed) {
      nextErrors.agreed =
        "Необходимо согласие на обработку персональных данных";
    }
    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitStatus({ type: "", message: "" });

    try {
      const response = await fetch("/api/v1/public/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          telephone: `+7${onlyDigits(formData.telephone)}`,
          subject,
          source,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success)
        throw new Error(data.message || "Ошибка отправки формы");

      setSubmitStatus({
        type: "success",
        message: "Заявка отправлена! Мы свяжемся с вами.",
      });
      setFormData(EMPTY_FORM_DATA);
      setAgreed(false);
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus({
        type: "error",
        message:
          "Не удалось отправить заявку. Напишите нам на info@ruscomtech.ru",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `${inputBase} ${
      errors[field] ? "ring-2 ring-red-500" : "focus:ring-2 focus:ring-iris"
    }`;

  return (
    <div
      className={`rounded-t-[25px] bg-white px-4 pb-[60px] pt-[50px] sm:px-16 md:px-[50px] ${className}`}
    >
      <h3 className="text-[21px] font-bold text-navy-900">{title}</h3>
      <p className="mt-5 text-[16px] font-medium leading-snug text-ink">
        {subtitle}
      </p>

      {submitStatus.message && (
        <div
          className={`mt-5 rounded-[10px] p-3 text-[14px] ${
            submitStatus.type === "success"
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {submitStatus.message}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        noValidate
        className="mt-5 flex flex-col gap-5"
      >
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field error={errors.name}>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Имя"
              aria-invalid={!!errors.name}
              className={inputClass("name")}
            />
          </Field>
          <Field error={errors.organisation}>
            <input
              type="text"
              name="organisation"
              value={formData.organisation}
              onChange={handleChange}
              placeholder="Компания"
              aria-invalid={!!errors.organisation}
              className={inputClass("organisation")}
            />
          </Field>
        </div>

        <Field error={errors.telephone}>
          <div className="relative">
            <span className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-[16px] font-medium text-ink">
              +7
            </span>

            <input
              type="tel"
              inputMode="numeric"
              maxLength={15}
              name="telephone"
              value={formData.telephone}
              onChange={handleChange}
              placeholder="(999) 999-99-99"
              aria-invalid={!!errors.telephone}
              className={`${inputClass("telephone")} !pl-[44px]`}
            />
          </div>
        </Field>

        <Field error={errors.email}>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="E-mail"
            aria-invalid={!!errors.email}
            className={inputClass("email")}
          />
        </Field>

        <Field error={errors.agreed}>
          <label className="flex cursor-pointer items-center gap-5">
            <input
              type="checkbox"
              checked={agreed}
              onChange={handleAgreedChange}
              aria-invalid={!!errors.agreed}
              className="peer sr-only"
            />
            <span
              className={`flex size-[26px] shrink-0 items-center justify-center rounded-[6px] bg-surface transition-colors peer-checked:bg-navy peer-focus-visible:ring-2 peer-focus-visible:ring-iris ${
                errors.agreed ? "ring-2 ring-red-500" : ""
              }`}
            >
              {agreed && (
                <svg
                  className="w-[14px]"
                  viewBox="0 0 14 11"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M1 5.5L5 9.5L13 1.5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
            <span className="text-[14px] font-medium leading-snug text-ink">
              Я соглашаюсь на обработку{" "}
              <Link
                to="/agreement"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2"
              >
                персональных данных
              </Link>
            </span>
          </label>
        </Field>

        <button
          type="submit"
          disabled={isSubmitting}
          className="h-[62px] w-full rounded-[10px] bg-brand-gradient text-[16px] font-medium text-white transition-opacity enabled:hover:bg-none enabled:hover:bg-coral disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? "Отправка..." : "Отправить заявку"}
        </button>
      </form>
    </div>
  );
}
